import React from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, Moon, Sun, Globe, ChevronDown } from "lucide-react";
import { useApp } from "../context/AppContext";
import { t, LANGUAGES } from "../lib/i18n";
import { COMPANY, ALL_SERVICES } from "../lib/data";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";

const NavItem = ({ to, children, onClick }) => (
  <NavLink
    to={to}
    onClick={onClick}
    data-testid={`nav-${to.replace(/\W/g, "-")}`}
    className={({ isActive }) =>
      `text-sm font-medium tracking-wide transition-colors hover:text-primary ${
        isActive ? "text-primary" : "text-foreground/80"
      }`
    }
  >
    {children}
  </NavLink>
);

export default function Header() {
  const { lang, setLang, theme, toggleTheme } = useApp();
  const [open, setOpen] = React.useState(false);

  return (
    <header className="sticky top-0 z-50 glass border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 sm:h-20 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <img src={COMPANY.logo} alt="Arthniti Financial Services" className="h-14 sm:h-16 w-auto object-contain shrink-0 drop-shadow-sm" />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-serif text-2xl sm:text-3xl font-semibold tracking-tight text-black dark:text-white">Arthniti</span>
              <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Better Ideas for Your Money</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            <NavItem to="/">{t(lang, "nav.home")}</NavItem>
            <NavItem to="/about">{t(lang, "nav.about")}</NavItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-services-dropdown" className="text-sm font-medium tracking-wide text-foreground/80 hover:text-primary inline-flex items-center gap-1">
                  {t(lang, "nav.services")} <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuItem asChild>
                  <Link to="/services" data-testid="nav-services-hub" className="font-semibold">{t(lang, "nav.servicesHub")}</Link>
                </DropdownMenuItem>
                {ALL_SERVICES.map((s) => (
                  <DropdownMenuItem asChild key={s.slug}>
                    <Link to={`/services/${s.slug}`} data-testid={`nav-service-${s.slug}`}>{t(lang, `services.titles.${s.slug}`) || s.title}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <NavItem to="/calculators">{t(lang, "nav.calculators")}</NavItem>
            <NavItem to="/career">{t(lang, "nav.career")}</NavItem>
            <NavItem to="/contact">{t(lang, "nav.contact")}</NavItem>
          </nav>

          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="lang-switcher-btn">
                  <Globe className="h-4 w-4 mr-1" />
                  <span className="text-xs uppercase">{lang}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {LANGUAGES.map((l) => (
                  <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} data-testid={`lang-option-${l.code}`}>
                    <span className="mr-2 text-xs uppercase text-muted-foreground">{l.code}</span> {l.native}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-btn" aria-label="Toggle theme">
              {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            </Button>

              <Link to="/contact" className="hidden md:inline-flex">
              <Button data-testid="header-cta-btn" className="bg-primary text-primary-foreground hover:brightness-110">
                {t(lang, "cta.book")}
              </Button>
            </Link>

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80">
                <div className="flex flex-col gap-4 mt-8">
                  <NavItem to="/" onClick={() => setOpen(false)}>{t(lang, "nav.home")}</NavItem>
                  <NavItem to="/about" onClick={() => setOpen(false)}>{t(lang, "nav.about")}</NavItem>
                  <NavItem to="/services" onClick={() => setOpen(false)}>{t(lang, "nav.services")}</NavItem>
                  <div className="pl-3 flex flex-col gap-2 border-l border-border">
                    {ALL_SERVICES.map((s) => (
                      <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => setOpen(false)} className="text-sm text-muted-foreground hover:text-primary" data-testid={`mobile-service-${s.slug}`}>{s.title}</Link>
                    ))}
                  </div>
                  <NavItem to="/calculators" onClick={() => setOpen(false)}>{t(lang, "nav.calculators")}</NavItem>
                  <NavItem to="/career" onClick={() => setOpen(false)}>{t(lang, "nav.career")}</NavItem>
                  <NavItem to="/contact" onClick={() => setOpen(false)}>{t(lang, "nav.contact")}</NavItem>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
